import { useState } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useBulkPricingActionsPermissions } from 'src/components/common/BulkActions/BulkPricingActions/useBulkPricingActionsPermissions';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useBulkAddHoldAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkAddHoldAction';
import { useBulkChangeEventAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeEventAction';
import { useBulkChangeListingPriceAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeListingPriceAction';
import { useBulkUpdateMarketplacePrices } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/indext';
import { useBulkChangeTicketTypeRuleAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeTicketTypeRuleAction';
import { useBulkEditAutoPricingSettings } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditAutoPricingSettingsAction';
import { useBulkEditBroadcastGlobalSettingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditBroadcastSettingsAction/useBulkEditBroadcastGlobalSettingsAction';
import { useBulkEditCommissionAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditCommissionAction/useBulkEditCommissionAction';
import { useBulkEditListingGroupSettings } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditListingGroupSettingsAction';
import { useBulkEditMarketplaceSeatingAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditMarketplaceSeating';
import { useBulkEditTagsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkEditTagsAction';
import { useBulkMergeSelectedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkMergeSelectedListingsAction';
import { useBulkRemoveHoldAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkRemoveHoldAction';
import { useBulkRemovePredelivery } from 'src/components/Listings/InventoryActionDropdown/components/BulkRemovePredeliveryAction';
import { useBulkResetMergedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkResetMergedListingsAction';
import { useBulkReturnTicketAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkReturnTicket';
import { useBulkSplitSelectedListingsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkSplitSelectedListingsAction';
import { useBulkUpdateAutoFulfillAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateAutoFulfillAction';
import { useBulkUpdateBarcodes } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateBarcodesAction/useBulkUpdateBarcodes';
import { useBulkUpdateDeliveryType } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateDeliveryType';
import { useBulkUpdateInternalNotesAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdateListingSettings } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateListingSettingsAction';
import { useBulkUpdateSeatTraits } from 'src/components/Listings/InventoryActionDropdown/components/BulkUpdateSeatTraits';
import { useBulkWasteTicketsAction } from 'src/components/Listings/InventoryActionDropdown/components/BulkWasteTicketsAction';
import { useBulkGroupListings } from 'src/components/Listings/InventoryActionDropdown/components/GroupListingsAction/useBulkGroupListings';
import { useBulkMoveToGroup } from 'src/components/Listings/InventoryActionDropdown/components/GroupListingsAction/useBulkMoveToGroup';
import { useBulkQuickGroup } from 'src/components/Listings/InventoryActionDropdown/components/GroupListingsAction/useBulkQuickGroup';
import { useBulkDeleteSeatSavers } from 'src/components/Listings/InventoryActionDropdown/components/LaunchDeleteSeatSaversAction';
import { useExportInventoryAction } from 'src/components/Listings/InventoryActionDropdown/components/LaunchExportInventoryAction/useExportInventory';
import { useBulkUngroupListings } from 'src/components/Listings/InventoryActionDropdown/components/LaunchUngroupListingsAction';
import { BulkActionGroup } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { useBulkChangeFloorCeilingActions } from 'src/hooks/useBulkChangeFloorCeilingActions';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { EventWithData, Feature, Permission } from 'src/WebApiController';

// Group Actions Hooks
const useListingGroupActions = (
  event: EventWithData,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(
    event?.event
  );
  const bulkGroupListings = useBulkGroupListings(event?.event);
  const bulkMoveToGroup = useBulkMoveToGroup(event);
  const bulkQuickGroup = useBulkQuickGroup(event);
  const bulkUngroupListings = useBulkUngroupListings(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    event?.event
  );
  const bulkEditListingGroupSettings = useBulkEditListingGroupSettings(
    filterQueryWithEventIds,
    event,
    setIsLoading,
    isLoading
  );

  const groupActions = [
    bulkEditListingGroupSettings.actionItems,
    bulkGroupListings.actionItems,
    bulkMoveToGroup.actionItems,
    bulkQuickGroup.actionItems,
    bulkUngroupListings.actionItems,
  ].flatMap((items) => items);

  if (groupActions.length > 0) {
    return {
      id: ContentId.Group,
      label: <Content id={ContentId.Group} />,
      actions: groupActions,
    };
  }
  return undefined;
};

const usePricingActions = (
  event: EventWithData | undefined,
  isEventPage = true,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
) => {
  const { hasEditPricingPermission } = useBulkPricingActionsPermissions();
  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState(isEventPage ? event?.event : undefined);

  const bulkUpdateListingPrice = useBulkChangeListingPriceAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkChangeFloorCeilingActions = useBulkChangeFloorCeilingActions({
    event: isEventPage ? event?.event : undefined,
    setIsLoading,
    isLoading,
    filterQueryWithEventIds,
  });
  const bulkEditAutoPricingSettingsV2 = useBulkEditAutoPricingSettings(
    filterQueryWithEventIds,
    affectedVisibleListingIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkUpdateMarketplacePrices = useBulkUpdateMarketplacePrices(
    filterQueryWithEventIds,
    affectedVisibleListingIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );

  const pricingActions = [
    bulkUpdateListingPrice.actionItems,
    ...(hasEditPricingPermission
      ? bulkChangeFloorCeilingActions.actionItems
      : []),
    bulkEditAutoPricingSettingsV2.actionItems,
    bulkUpdateMarketplacePrices.actionItems,
  ].flatMap((items) => items);

  if (pricingActions.length > 0) {
    return {
      id: ContentId.Pricing,
      label: <Content id={ContentId.Pricing} />,
      actions: pricingActions,
    };
  }

  return undefined;
};

const useBroadcastActionGroup = (
  event: EventWithData,
  isEventPage: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
) => {
  const { hasBroadcastPermission } = useBulkBroadcastActionsPermission();
  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(
    isEventPage ? event?.event : undefined
  );

  const bulkAddHold = useBulkAddHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkRemoveHold = useBulkRemoveHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditBroadcastSettings = useBulkEditBroadcastGlobalSettingsAction({
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
  });
  const bulkEditBroadcastSettingGlobal =
    useBulkEditBroadcastGlobalSettingsAction({
      filterQueryWithEventIds,
      setIsLoading,
      isLoading,
    });
  const bulkSplitToOriginalAction = useBulkResetMergedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkMergeSelectedListingsAction = useBulkMergeSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkSplitSelectedListingsAction = useBulkSplitSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );

  // Broadcast Actions (only if user has permission)
  if (hasBroadcastPermission) {
    const broadcastActions = [
      isEventPage
        ? bulkEditBroadcastSettings.actionItems
        : bulkEditBroadcastSettingGlobal.actionItems,
      bulkAddHold.actionItems,
      bulkRemoveHold.actionItems,
      bulkSplitToOriginalAction.actionItems,
      bulkMergeSelectedListingsAction.actionItems,
      bulkSplitSelectedListingsAction.actionItems,
    ].flatMap((items) => items);

    if (broadcastActions.length > 0) {
      return {
        id: ContentId.Broadcast,
        label: <Content id={ContentId.Broadcast} />,
        actions: broadcastActions,
      };
    }
  }

  return undefined;
};

const useTagsActionGroup = (
  event: EventWithData | undefined,
  isEventPage: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
) => {
  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(
    isEventPage ? event?.event : undefined
  );

  // Tags Actions Hooks
  const bulkUpdateTags = useBulkEditTagsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );

  // Tags Actions
  const tagsActions = [bulkUpdateTags.actionItems].flatMap((items) => items);

  if (tagsActions.length > 0) {
    return {
      id: ContentId.Tags,
      label: <Content id={ContentId.Tags} />,
      actions: tagsActions,
    };
  }

  return undefined;
};

// Settings Actions Hooks
const useOtherSettingsActionGroup = (
  event: EventWithData | undefined,
  isEventPage: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
) => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const hasExportInventoryFeature = useUserHasFeature(Feature.ExportInventory);
  const hasInventoryExportPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_Export
  );
  const hasBulkReturnTicketFeature = useUserHasFeature(
    Feature.BulkReturnTicket
  );
  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState(isEventPage ? event?.event : undefined);

  const bulkEditGeneralSettings = useBulkUpdateListingSettings(
    'general',
    filterQueryWithEventIds,
    affectedVisibleListingIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkUpdateAutoFulfill = useBulkUpdateAutoFulfillAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkRemovePredelivery = useBulkRemovePredelivery(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkUpdateBarcodesAction = useBulkUpdateBarcodes(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkDeleteSeatSavers = useBulkDeleteSeatSavers(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditDeliverType = useBulkUpdateDeliveryType(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditSeatTraits = useBulkUpdateSeatTraits(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkChangeTicketTypeRule = useBulkChangeTicketTypeRuleAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditMarketplaceSeating = useBulkEditMarketplaceSeatingAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkReturnTicketAction = useBulkReturnTicketAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const exportInventoryAction = useExportInventoryAction({
    textContentId: ContentId.ExportListings,
    textOnly: true,
    disabled: !areBulkActionsEnabled,
    shape: 'rect',
    variant: 'text',
    style: { color: vars.color.textStrong },
  });

  // Settings Actions
  const settingsActions = [
    bulkEditGeneralSettings.actionItems,
    bulkUpdateInternalNotes.actionItems,
    bulkUpdateAutoFulfill.actionItems,
    bulkRemovePredelivery.actionItems,
    bulkUpdateBarcodesAction.actionItems,
    bulkDeleteSeatSavers.actionItems,
    bulkEditDeliverType.actionItems,
    bulkEditSeatTraits.actionItems,
    bulkChangeTicketTypeRule.actionItems,
    bulkEditMarketplaceSeating.actionItems,
    bulkEditCommissionAction.actionItems,
    ...(hasBulkReturnTicketFeature ? [bulkReturnTicketAction.actionItems] : []),
    ...(hasExportInventoryFeature && hasInventoryExportPermission && isEventPage
      ? [exportInventoryAction.actionItems]
      : []),
  ].flatMap((items) => items);

  if (settingsActions.length > 0) {
    return {
      id: ContentId.Other,
      label: <Content id={ContentId.Other} />,
      actions: settingsActions,
    };
  }

  return undefined;
};

const useDestructiveActionGroup = (
  event: EventWithData | undefined,
  isEventPage: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(
    isEventPage ? event?.event : undefined
  );
  const bulkChangeEvent = useBulkChangeEventAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkWasteTicketsAction = useBulkWasteTicketsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );

  const destructiveActions = [
    bulkChangeEvent.actionItems,
    bulkWasteTicketsAction.actionItems,
  ].flatMap((items) => items);

  if (destructiveActions.length > 0) {
    return {
      id: ContentId.Warning,
      label: <Content id={ContentId.Warning} />,
      actions: destructiveActions,
      destructive: true,
    };
  }

  return undefined;
};

export const useListingBulkActions = (
  event: EventWithData,
  isEventPage = true
): BulkActionGroup[] => {
  const [isLoading, setIsLoading] = useState(false);

  const actionGroups: BulkActionGroup[] = [];

  const listingGroupActionGroup = useListingGroupActions(
    event,
    isLoading,
    setIsLoading
  );
  const pricingActionGroup = usePricingActions(
    event,
    isEventPage,
    isLoading,
    setIsLoading
  );
  const broadcastActionGroup = useBroadcastActionGroup(
    event,
    isEventPage,
    isLoading,
    setIsLoading
  );

  const tagsActionGroup = useTagsActionGroup(
    event,
    isEventPage,
    isLoading,
    setIsLoading
  );

  const otherSettingsActionGroup = useOtherSettingsActionGroup(
    event,
    isEventPage,
    isLoading,
    setIsLoading
  );

  const destructiveActionGroup = useDestructiveActionGroup(
    event,
    isEventPage,
    isLoading,
    setIsLoading
  );

  if (listingGroupActionGroup) {
    actionGroups.push(listingGroupActionGroup);
  }

  if (pricingActionGroup) {
    actionGroups.push(pricingActionGroup);
  }
  if (broadcastActionGroup) {
    actionGroups.push(broadcastActionGroup);
  }
  if (tagsActionGroup) {
    actionGroups.push(tagsActionGroup);
  }
  if (otherSettingsActionGroup) {
    actionGroups.push(otherSettingsActionGroup);
  }
  if (destructiveActionGroup) {
    actionGroups.push(destructiveActionGroup);
  }

  return actionGroups;
};

const useInventoryBroadcastActionGroup = (
  event: EventWithData | undefined,
  isEventPage: boolean,
  isLoading: boolean,
  setIsLoading: (_: boolean) => void
): BulkActionGroup | undefined => {
  const { hasBroadcastPermission } = useBulkBroadcastActionsPermission();
  const { filterQueryWithEventIds } = useInventoryEventBulkActionsState(
    isEventPage ? event?.event : undefined
  );

  const bulkAddHold = useBulkAddHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkRemoveHold = useBulkRemoveHoldAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkEditBroadcastSettingGlobal =
    useBulkEditBroadcastGlobalSettingsAction({
      filterQueryWithEventIds,
      setIsLoading,
      isLoading,
    });
  const bulkSplitToOriginalAction = useBulkResetMergedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkMergeSelectedListingsAction = useBulkMergeSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );
  const bulkSplitSelectedListingsAction = useBulkSplitSelectedListingsAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    isEventPage ? event?.event : undefined
  );

  // Broadcast Actions (only if user has permission)
  if (hasBroadcastPermission) {
    const broadcastActions = [
      bulkEditBroadcastSettingGlobal.actionItems,
      bulkAddHold.actionItems,
      bulkRemoveHold.actionItems,
      bulkSplitToOriginalAction.actionItems,
      bulkMergeSelectedListingsAction.actionItems,
      bulkSplitSelectedListingsAction.actionItems,
    ].flatMap((items) => items);

    if (broadcastActions.length > 0) {
      return {
        id: ContentId.Broadcast,
        label: <Content id={ContentId.Broadcast} />,
        actions: broadcastActions,
      };
    }
  }

  return undefined;
};

export const useInventoryListingBulkActions = (): BulkActionGroup[] => {
  const [isLoading, setIsLoading] = useState(false);

  const actionGroups: BulkActionGroup[] = [];

  const pricingActionGroup = usePricingActions(
    undefined,
    false,
    isLoading,
    setIsLoading
  );
  const broadcastActionGroup = useInventoryBroadcastActionGroup(
    undefined,
    false,
    isLoading,
    setIsLoading
  );

  const tagsActionGroup = useTagsActionGroup(
    undefined,
    false,
    isLoading,
    setIsLoading
  );

  const otherSettingsActionGroup = useOtherSettingsActionGroup(
    undefined,
    false,
    isLoading,
    setIsLoading
  );

  const destructiveActionGroup = useDestructiveActionGroup(
    undefined,
    false,
    isLoading,
    setIsLoading
  );

  if (pricingActionGroup) {
    actionGroups.push(pricingActionGroup);
  }
  if (broadcastActionGroup) {
    actionGroups.push(broadcastActionGroup);
  }
  if (tagsActionGroup) {
    actionGroups.push(tagsActionGroup);
  }
  if (otherSettingsActionGroup) {
    actionGroups.push(otherSettingsActionGroup);
  }
  if (destructiveActionGroup) {
    actionGroups.push(destructiveActionGroup);
  }

  return actionGroups;
};
